import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Chip, IconButton, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Check as CheckIcon, Clear as ClearIcon } from '@mui/icons-material';
import moment from 'moment/moment';
import deepmerge from 'deepmerge';

import { DuplicateButton } from '../../../common/button';
import { GET_ROUTE_PROMOTION_CAMPAIGNS_FORMS, MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT, ROUTE_PROMOTION_CAMPAIGNS_FORMS_NEW } from '../../../../core/constants';
import { getPromotionCampaignsAction, getPromotionCampaignsState } from '../../../../features/price/get_promotion_campaigns';
import { getPromotionCampaignStatus } from '../../../common/utils/promotion_campaign_utils';
import { getPromotionCampaignsNotificationData } from '../../../../core/utils/notification_utils';
import useNotification from '../../../../core/hooks/use_notification';
import PaginationControls from '../../../common/pagination_controls';
import { FormRouteEnum } from '../../../../core/enums/common/route';

const ArchivedPromotionCampaignsList = ({ tab, queryParams, setQueryParams }) => {
    const { showErrorMessage } = useNotification();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const getPromotionCampaigns = useSelector(getPromotionCampaignsState);

    const refreshPromotionCampaignList = useCallback(() => {
        dispatch(getPromotionCampaignsAction(queryParams))
            .then(response => showErrorMessage(getPromotionCampaignsNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        refreshPromotionCampaignList();
    }, [queryParams, refreshPromotionCampaignList]);

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
            { getPromotionCampaigns?.loading && <LinearProgress/> }

            {
                getPromotionCampaigns?.data && (
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell>Nazwa robocza</TableCell>
                                    <TableCell align="center">Data obowiązywania</TableCell>
                                    <TableCell align="center">Powtarzalna</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="right" sx={ { pr: 3 } }></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    getPromotionCampaigns?.data?.content?.map(promotionCampaign => (
                                        <TableRow key={ promotionCampaign?.id } hover>
                                            <TableCell
                                                sx={ { fontWeight: 500 } }>{ promotionCampaign?.workingName }</TableCell>
                                            <TableCell align="center">
                                                <Box sx={ {
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center'
                                                } }>
                                                    <Typography variant="body2">
                                                        Od:&nbsp;{ moment(promotionCampaign?.timeFrom, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Do:&nbsp;{ moment(promotionCampaign?.timeTo, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                { promotionCampaign?.repeatable ?
                                                    <CheckIcon color="success" fontSize="small"/> :
                                                    <ClearIcon color="error" fontSize="small"/> }
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={ getPromotionCampaignStatus(promotionCampaign?.status) }
                                                    color="default"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={ { pr: 2 } }>
                                                <Stack direction="row" spacing={ 1 } justifyContent="flex-end"
                                                       alignItems="center">
                                                    <DuplicateButton size="small"
                                                                     onClick={ () => navigate(ROUTE_PROMOTION_CAMPAIGNS_FORMS_NEW, {
                                                                         state: {
                                                                             action: FormRouteEnum.NEW,
                                                                             promotionCampaignIdToDuplicate: promotionCampaign?.id,
                                                                             queryParams,
                                                                             tab
                                                                         }
                                                                     }) }/>
                                                    <Tooltip title="Podgląd">
                                                        <IconButton size="small"
                                                                    onClick={ () => navigate(GET_ROUTE_PROMOTION_CAMPAIGNS_FORMS(promotionCampaign?.id), {
                                                                        state: {
                                                                            action: FormRouteEnum.VIEW,
                                                                            queryParams,
                                                                            tab
                                                                        }
                                                                    }) }>
                                                            <SearchIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
            }
            {
                getPromotionCampaigns?.data && (
                    <Box sx={ { mt: 2, display: 'flex', justifyContent: 'center' } }>
                        <PaginationControls
                            currentPage={ getPromotionCampaigns?.data?.pageable?.pageNumber }
                            totalPages={ getPromotionCampaigns?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                )
            }
        </Box>
    );
};

export default ArchivedPromotionCampaignsList;